import type { WebinarEventHistoryDto } from '@/features/live/types';
import type { SharedResourcePublic } from '@/features/webinar/types';

export interface ActiveSharedResource {
  resourceId: number;
  isVisible: boolean;
  resource: SharedResourcePublic;
}

/**
 * Ressource partagée active à `currentTime` pendant un replay : une seule à la fois, comme
 * l'offre/le quiz/le sondage (l'admin masque les autres avant d'en afficher une nouvelle, cf.
 * SharedResourceLiveList.handleToggle côté front). Le DTO complet de la ressource est embarqué
 * directement dans le payload de l'event RESOURCE_SHOW (pas besoin d'un fetch séparé — il n'existe
 * d'ailleurs pas d'endpoint public pour récupérer une ressource partagée par id, contrairement à
 * l'offre/au quiz/au sondage), cf. SharedResourceServiceImpl.writeLastPayloadJson côté back.
 */
export const getActiveSharedResource = (
  eventHistory: WebinarEventHistoryDto | null,
  currentTime: number,
): ActiveSharedResource | null => {
  if (!eventHistory?.events) {
    return null;
  }

  const resourceEvents = eventHistory.events.filter(
    (event) =>
      event.payload?.resourceType === 'SHARED_RESOURCE' &&
      (event.eventType === 'RESOURCE_SHOW' || event.eventType === 'RESOURCE_HIDE'),
  );

  if (resourceEvents.length === 0) {
    return null;
  }

  const sortedEvents = resourceEvents.sort((a, b) => {
    if (a.offsetSeconds !== b.offsetSeconds) {
      return a.offsetSeconds - b.offsetSeconds;
    }
    const aResourceId = (a.payload?.resourceId as number | undefined) || 0;
    const bResourceId = (b.payload?.resourceId as number | undefined) || 0;
    return aResourceId - bResourceId;
  });

  for (let i = sortedEvents.length - 1; i >= 0; i--) {
    const event = sortedEvents[i];
    if (event.offsetSeconds > currentTime) {
      continue;
    }

    const resourceId = event.payload?.resourceId as number | undefined;
    if (!resourceId) continue;

    if (event.eventType === 'RESOURCE_SHOW') {
      const showTime = event.offsetSeconds;
      const hideEvent = sortedEvents.find(
        (e) =>
          e.offsetSeconds > showTime &&
          e.offsetSeconds <= currentTime &&
          e.eventType === 'RESOURCE_HIDE' &&
          (e.payload?.resourceId as number | undefined) === resourceId,
      );

      if (!hideEvent) {
        const resource = event.payload?.resource as SharedResourcePublic | undefined;
        if (!resource) continue;
        return { resourceId, isVisible: true, resource };
      }
    }
  }

  return null;
};
